import React, { useRef, useEffect, useMemo } from "react";
import Highcharts from "highcharts";
import initExporting from "highcharts/modules/exporting";
import initOfflineExporting from "highcharts/modules/offline-exporting";
import HighchartsReact from "highcharts-react-official";
import { Empty, Typography } from "antd";
import { useSlicerVariables, useWidgetData } from "../Contexts/dashboardHooks";
import { useChartSync } from "../Contexts/ChartSyncContext";
import { WidgetInfoButton } from "../Info/WidgetInfoButton";
import { resolveTemplate } from "../Utils/resolveTemplate";

initExporting(Highcharts);
initOfflineExporting(Highcharts);

const { Text } = Typography;

const CHART_CONTAINER_STYLE: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
};

export function PieWidget({ id }: { id: string }) {
  const data = useWidgetData(id);
  const chartRef = useRef<HighchartsReact.RefObject | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const syncContext = useChartSync();
  const requestSyncRef = useRef<() => void>(() => {});
  if (syncContext) requestSyncRef.current = syncContext.requestSync;

  const slicerValues = useSlicerVariables();
  const rawTitle = data?.type === "pie_chart" ? data.spec.display.title : null;
  const chartTitle = rawTitle ? resolveTemplate(rawTitle, slicerValues).resolved : null;

  const options = useMemo(() => {
    if (!data || data.type !== "pie_chart") return null;
    return {
      ...data.chart.chartOptions,
      chart: {
        ...((data.chart.chartOptions.chart as Record<string, unknown>) ?? {}),
        reflow: true,
        events: {
          load() {
            requestSyncRef.current();
          },
        },
      },
      title: { text: "" },
    };
  }, [data]);

  useEffect(() => {
    if (!syncContext) return;
    const getCell = () =>
      containerRef.current?.closest(".grid-item-wrapper") ?? containerRef.current ?? null;
    const getChart = () => chartRef.current?.chart ?? null;

    syncContext.register(id, getCell, getChart);
    const t = setTimeout(() => syncContext.requestSync(), 200);

    return () => {
      clearTimeout(t);
      syncContext.unregister(id);
    };
  }, [id, syncContext]);

  const hasData = useMemo(() => {
    if (!options) return false;
    const series = options.series as Array<{ data?: unknown[] }> | undefined;
    return Array.isArray(series) && series.some((s) => (s.data?.length ?? 0) > 0);
  }, [options]);

  if (!options) {
    return (
      <div className="pie-widget" data-grid-id={id} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No chart data" />
      </div>
    );
  }

  return (
    <div ref={containerRef} className="pie-widget" data-grid-id={id}>
      <div className="widget-header">
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          {chartTitle && <Text strong style={{ fontSize: 18 }}>{chartTitle}</Text>}
          <WidgetInfoButton id={id} />
        </span>
      </div>
      <div className="chart-widget-body" style={{ position: "relative" }}>
        <HighchartsReact
          highcharts={Highcharts}
          options={options}
          ref={chartRef}
          containerProps={{ style: CHART_CONTAINER_STYLE }}
        />
        {!hasData && (
          <div style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              No data found in the specified date range
            </Text>
          </div>
        )}
      </div>
    </div>
  );
}
